import { resourceAssignments, resourceStaffGroups, resourceStaffGroupsInAA } from '../../api/resources';
import {
    USER_TYPES,
    CARD_LEVEL_START_VALUE,
    CARD_LEVELS_NUMBER,
    CARD_LEVEL_ANONYMOUS_VALUE,
    DEFINED_CARD_LEVEL_VALUES,
} from '../../constants';

/**
 * Creates new Staff with empty parameters
 */
export function getEmptyStaffMember() {
    return {
        user_id: null,
        login_name: '',
        password: '',
        verifyPassword: '',
        first_name: '',
        last_name: '',
        language: '',
        areas: null,
        message_group_id: null,
        call_id: null,
        card_id: null,
        card_action: CARD_LEVEL_ANONYMOUS_VALUE,
        card_access: [], // access module ids
        accessModules: [],
        assignable: false,
        type: USER_TYPES.USER,
        comment: '',
        isAAPrimary: true, // if true then staff was created in current AA
        assigned: false,
        assignedGroupNames: [],
        userIsInAssignedGroup: false,
        userIsLastAssignableInAssignedGroup: false,
        loginIsChanged: false, // if true then password should be set
        fromCurrentAA: false, // if true then staff will be display on screen
        editing: false,
        staffGroups: [],
    };
}

/**
 * Creates cardLevelOptions which contains one anonymus option with value '0'
 * and ten options with value from '120' to '129'
 */
export function getCardLevelOptions() {
    let cardLevelOptions = [];
    cardLevelOptions.push({
        value: CARD_LEVEL_ANONYMOUS_VALUE,
        name: this.$t('staff.membersTab.cardLevelOptions.anonymous'),
    });
    for (let value = CARD_LEVEL_START_VALUE; value < CARD_LEVEL_START_VALUE + CARD_LEVELS_NUMBER; value++) {
        cardLevelOptions.push({
            value,
            name: this.$t('staff.membersTab.cardLevelOptions.level', {
                val: value - CARD_LEVEL_START_VALUE + 1,
            }),
        });
    }
    return cardLevelOptions;
}

export function updateStaffMembers(staffMembers) {
    return staffMembers.map(staffMember => {
        let card_action = DEFINED_CARD_LEVEL_VALUES.includes(staffMember.card_action)
            ? staffMember.card_action
            : CARD_LEVEL_ANONYMOUS_VALUE;
        return { ...staffMember, card_action };
    });
}

export default {
    data() {
        return {
            assignments: [],
            allStaffGroups: [],
            staffGroupsInAA: [],
        };
    },

    methods: {
        /**
         *
         * @param id - Numeric (ID of user or group)
         * @param used - boolean (true  if used in assignments)  by default - false
         * @param kind - String (can be 'users' or 'groups') by default 'users'
         * @returns {boolean} user or group was found in any assignment
         */
        isAssigned(id, used = false, kind = 'users') {
            if (!id) {
                return false;
            }
            if (used) {
                return true;
            }

            return this.assignments.some(location => {
                let assignments = location.assignments;

                return ['direct', 'duty'].some(assignmentType => {
                    let assignment = assignments[assignmentType];
                    if (assignment) {
                        return ['observers', 'assistance'].some(callType => {
                            let call = assignment[callType];

                            // direct
                            if (call && Array.isArray(call)) {
                                return call.some(team => {
                                    return team[kind] && team[kind].includes(id);
                                });
                            }

                            // duty
                            return Object.keys(assignment).some(shiftId => {
                                let shift = assignment[shiftId];
                                if (shift && shift[callType]) {
                                    return shift[callType].some(chain => {
                                        return chain[kind] && chain[kind].includes(id);
                                    });
                                }

                                return false;
                            });
                        });
                    }

                    return false;
                });
            });
        },

        /**
         * Get names of staff groups, used in assignments, and in which
         *      the user (identified by userId) is the last assignable member.
         * @param {number} userId User ID
         * @returns {Array<string>} array of staff group names
         */
        getAssignedGroupNames(userId) {
            if (userId) {
                let groupIds = this.getGroupIdsWithLastUser(userId);
                let assignedGroupIds = groupIds.filter(groupId => this.isAssigned(groupId, false, 'groups'));
                if (assignedGroupIds.length > 0) {
                    return this.staffGroupsInAA
                        .filter(group => assignedGroupIds.includes(group.staff_group_id))
                        .map(group => group.name);
                }
            }

            return [];
        },

        /**
         * Check if the user (identified by userId) is the last assignable staff member of some assigned staff group
         * @param {number} userId User ID
         * @returns {boolean} true if we found assigned group, where the user is the last assignable member
         */
        isLastAssignableUserInAssignedGroup(userId) {
            return this.getAssignedGroupNames(userId).length > 0;
        },

        /**
         * Get IDs of staff groups, that include the user (identified by userId),
         *      and do not include other assignable users.
         * @param {number} userId User ID
         * @returns {Array<number>} array of staff group IDs
         */
        getGroupIdsWithLastUser(userId) {
            let groups = this.staffGroupsInAA.filter(group => {
                let users = group.users || [];
                let isUserInGroup = users.some(user => user.user_id === userId);

                if (!isUserInGroup) {
                    return false;
                }

                let otherWithNumber = users.filter(
                    user => user.user_id !== userId && user.message_group && user.assignable
                );

                // return false if there are other assignable staff with number in group
                return otherWithNumber.length === 0;
            });

            return groups.map(group => group.staff_group_id);
        },

        getGroupsWithUser(userId) {
            return this.staffGroupsInAA
                .filter(group => group.users && group.users.some(user => user.user_id === userId))
                .map(group => ({
                    groupId: group.staff_group_id,
                    name: group.name,
                }));
        },

        getGroupNamesWithUser(userId) {
            return this.staffGroupsInAA
                .filter(group => group.users && group.users.some(user => user.user_id === userId))
                .map(group => group.name);
        },

        /**
         * removes staff member from staff group
         * @param {number} groupId staff group id
         * @param {number} staffId staff id
         */
        removeStaffMemberFromStaffGroup(groupId, staffId) {
            let staffGroupIndex = this.staffGroupsInAA.findIndex(group => {
                return group.staff_group_id === groupId;
            });

            if (staffGroupIndex !== -1) {
                let users = this.staffGroupsInAA[staffGroupIndex].users || [];
                let userIndex = users.findIndex(user => user.user_id === staffId);

                if (userIndex !== -1) {
                    this.staffGroupsInAA[staffGroupIndex].users.splice(userIndex, 1);
                }
            }
        },

        /**
         *
         * @param {Object} group { groupId: 100, name: 'groupName' }
         * @param {*} staff
         */
        updateStaffInGroup(staffGroup, staff) {
            let staffGroupIndex = this.staffGroupsInAA.findIndex(group => {
                return group.staff_group_id === staffGroup.groupId;
            });

            if (staffGroupIndex !== -1) {
                let users = this.staffGroupsInAA[staffGroupIndex].users || [];
                let userIndex = users.findIndex(user => user.user_id === staff.user_id);

                if (userIndex !== -1) {
                    this.staffGroupsInAA[staffGroupIndex].users[userIndex].assignable = staff.assignable;
                    this.staffGroupsInAA[staffGroupIndex].users[userIndex].message_group =
                        staff.message_group_id;
                }
            }
        },

        isUserInAssignedGroup(userId) {
            if (!userId) {
                return false;
            }
            return (
                this.staffGroupsInAA.filter(
                    group =>
                        group.users &&
                        group.users.some(user => user.user_id === userId) &&
                        this.isAssigned(group.staff_group_id, false, 'groups')
                ).length > 0
            );
        },

        loadAssignments(areaId) {
            return resourceAssignments({
                id: areaId,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.assignments = response.data || [];
                })
                .catch(() => {
                    this.assignments = [];
                });
        },

        loadStaffGroupsList() {
            return resourceStaffGroups({ cancelToken: this.cancelToken.token })
                .then(response => {
                    this.allStaffGroups = response.data || [];
                })
                .catch(response => {
                    this.allStaffGroups = [];

                    return Promise.reject(response);
                });
        },

        loadStaffGroupsInAA(areaId) {
            return resourceStaffGroupsInAA({
                id: areaId,
                cancelToken: this.cancelToken ? this.cancelToken.token : undefined,
            })
                .then(response => {
                    this.staffGroupsInAA = response.data || [];
                })
                .catch(response => {
                    this.staffGroupsInAA = [];

                    return Promise.reject(response);
                });
        },
    },
};
